<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('ProjectSettings.CompanyUsersTab.InviteUserModal.title') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full">
        <form
          class="flex flex-col space-y-4"
          @submit.prevent="inviteUser"
        >
          <label>
            {{ $t('ProjectSettings.CompanyUsersTab.InviteUserModal.email') }}

            <div class="relative">
              <AppSpinner
                v-if="searchUsersAction.is(States.LOADING)"
                class="absolute my-3 right-2"
                spinner-class="h-4 w-4"
              />

              <AppInput
                v-model="userForm.form.email"
                type="text"
                name="email"
                class="form-input pr-8"
                :error="userForm.getErrors('email')"
              />
            </div>

            <p
              v-if="userFound"
              class="text-xs text-ccm-blue-500 font-semibold"
            >
              {{ $t('ProjectSettings.CompanyUsersTab.InviteUserModal.userFound') }}
            </p>
          </label>

          <label>
            {{ $t('ProjectSettings.CompanyUsersTab.InviteUserModal.name') }}
            <AppInput
              v-model="userForm.form.name"
              type="text"
              name="name"
              class="form-input"
              :error="userForm.getErrors('name')"
              :disabled="userFound"
            />
          </label>

          <div>
            <p class="font-medium">
              {{ $t('ProjectSettings.CompanyUsersTab.InviteUserModal.projects') }}
            </p>

            <div class="flex items-center space-x-4">
              <label class="mb-auto">
                {{ $t('ProjectSettings.CompanyUsersTab.InviteUserModal.project') }}

                <AppSelect
                  v-model="userForm.form.project_id"
                  class="form-input pr-8"
                  name="project_id"
                  :options="availableProjects"
                  :error="userForm.getErrors('project_id')"
                />
              </label>

              <label class="mb-auto">
                {{ $t('ProjectSettings.CompanyUsersTab.InviteUserModal.role') }}

                <AppSelect
                  v-model="userForm.form.role_id"
                  class="form-input pr-8"
                  name="role_id"
                  :options="roles"
                  :error="userForm.getErrors('role_id')"
                />
              </label>

              <button
                type="button"
                class="btn btn--secondary-blue mt-auto"
                @click.prevent="addRole"
              >
                {{ $t('ProjectSettings.CompanyUsersTab.InviteUserModal.addProject') }}
              </button>
            </div>
          </div>

          <ul>
            <li
              v-for="(project, i) in userForm.form.projects"
              :key="i"
            >
              <div class="flex items-center">
                <div>
                  {{ projectsStore.models.find(project.project_id)?.name }} {{ $t('ProjectSettings.CompanyUsersTab.InviteUserModal.as') }} {{ rolesStore.models.find(project.role_id)?.descriptor }}
                </div>

                <button
                  type="button"
                  @click="removeProject(i)"
                >
                  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5 btn-icon-red">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
                </button>
              </div>
            </li>
          </ul>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close')"
        >
          {{ $t('ProjectSettings.CompanyUsersTab.InviteUserModal.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="userForm.form.projects.length === 0"
          @click="inviteUser"
        >
          {{ $t('ProjectSettings.CompanyUsersTab.InviteUserModal.invite') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import { useProjectsStore } from '@/store/projects';
import { useRolesStore } from '@/store/roles';
import { useUserStore } from '@/store/user';
import { useDebounceFn } from '@vueuse/core';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  companyId: number;
}>();

const projectsStore = useProjectsStore();
const userStore = useUserStore();
const rolesStore = useRolesStore();

rolesStore.fetchRoles();

const searchUsersAction = useStoreApiAction(userStore.searchUsers);
const inviteUserAction = useStoreApiAction(userStore.inviteUser);

const userForm = useFormData({
  name: '',
  email: '',
  projects: ([] as { role_id: number; project_id: number }[]),
  project_id: undefined,
  role_id: undefined,
});

const userFound = ref(false);

const searchClient = useDebounceFn(async () => {
  searchUsersAction.request(props.companyId, {
    email: userForm.form.email,
  }).then((userToInvite) => {
    console.log(userToInvite);

    if(userToInvite?.id) {
      userFound.value = true;
      userForm.setData(userToInvite, ['email', 'projects', 'project_id', 'role_id']);
    } else {
      userFound.value = false;
      userForm.form.name = '';
    }
  });
}, 500);

watch(() => userForm.form.email, (newValue) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if(newValue && emailRegex.test(newValue)) {
    searchClient();
  }
});

const inviteUser = () => {
  inviteUserAction.request(props.companyId, userForm.form).then(() => {
    Swal.fire({
      icon: 'success',
      title: t('ProjectSettings.CompanyUsersTab.InviteUserModal.userInvited'),
      text: t('ProjectSettings.CompanyUsersTab.InviteUserModal.invitationEmailSent', { name: userForm.form.name }),
    });

    userForm.resetData();
    userForm.form.projects = [];
    userFound.value = false;
  }).catch((error) => {
    if(error?.status === 422) {
      userForm.setErrors(error.data);
    } else if(error?.status === 403) {
      Swal.fire({
        icon: 'error',
        text: error.data.message,
      });
    }
  });
};

const companyProjectsWithNoRole = computed(() => {
  return projectsStore.models.query()
    .where('company_id', props.companyId)
    .get()
    .map((project) => {
      return {
        label: project.name,
        value: project.id,
      };
    });
});

const availableProjects = computed(() => {
  return companyProjectsWithNoRole.value.filter((project) => {
    return userForm.form.projects.findIndex((selectedProject) => selectedProject.project_id === project.value) === -1;
  });
});

const roles = computed(() => {
  const descriptions: { [K in RoleTypes]: string } = {
    admin: '',
    client_manager: '',
    company_admin: '',
    contract_admin: 'Has full access to head contract module, can modify all head contract settings.',
    contract_manager: `Can access head contract module, but cannot update the contract sum after it is locked.
        Can modify notice clauses.`,
    engineer: `Has full access to self perform module, can modify all self perform settings.`,
    project_admin: `Has full access to every module and can modify any settings related to the project.`,
    site_manager: `Has access to labour and plant records.`,
  };

  return rolesStore.models.whereNotIn('name', ['admin', 'company_admin', 'client_manager']).get().map(
    (role) => {
      return {
        label: role.descriptor,
        value: role.id,
        title: descriptions[role.name],
      };
    },
  );
});

const addRole = () => {
  if(userForm.form.project_id && userForm.form.role_id) {
    userForm.form.projects.push({
      project_id: userForm.form.project_id,
      role_id: userForm.form.role_id,
    });

    userForm.form.project_id = undefined;
    userForm.form.role_id = undefined;
  }
};

const removeProject = (index: number) => {
  userForm.form.projects.splice(index, 1);
};
</script>

<style scoped></style>
